import { FormProvider } from "react-hook-form";

import { toastActions } from "src/design-system/Toast/utils";
import { useUpdateEntitySchema } from "src/entities/queries";
import { SchemaBaseEditor } from "src/entities/SchemaEditor/SchemaBaseEditor";
import { SchemaPropertiesEditor } from "src/entities/SchemaEditor/SchemaPropertiesEditor";
import { remapNewProperties } from "src/entities/SchemaEditor/utils";
import { useSchemaEditor } from "src/entities/useSchemaEditor";
import { SchemaEditorSidebar } from "src/schema/schemaEditor/SchemaEditorSidebar";
import { EntitySchemaFormData } from "src/schema/schemaEditor/types";
import { useSchemaForm } from "src/schema/schemaEditor/useSchemaForm";
import { toastError } from "src/utils/toastError";

type EntitySchemaEditorSidebarProps = {
  schema: EntitySchemaFormData;
};

export const EntitySchemaEditorSidebar: React.FC<
  EntitySchemaEditorSidebarProps
> = ({ schema }) => {
  const { mutateAsync: updateSchema } = useUpdateEntitySchema();
  const [, toggleSchemaEditor] = useSchemaEditor();

  const { form, handleSubmit, isSaving, canSave } = useSchemaForm(
    schema,
    async (data) => {
      const result = await updateSchema(remapNewProperties(data));
      return result as EntitySchemaFormData;
    },
  );

  const onSubmit = form.handleSubmit(async (data) => {
    try {
      await handleSubmit(data);
      toastActions.success({ title: "Entity schema updated" });
    } catch (error) {
      toastError(error);
    }
  });

  return (
    <form className="h-full" onSubmit={onSubmit}>
      <FormProvider {...form}>
        <SchemaEditorSidebar
          canSave={canSave}
          isSaving={isSaving}
          schema={schema}
          title="Edit Entity schema"
          tooltipText="For additional changes you want to make to the Entity Schema, please contact the Taktile team."
          onClose={toggleSchemaEditor}
        >
          <div className="flex h-full flex-col gap-y-4">
            <SchemaBaseEditor isLoading={false} />
            <SchemaPropertiesEditor isLoading={false} />
          </div>
        </SchemaEditorSidebar>
      </FormProvider>
    </form>
  );
};
